import { FileRejection } from 'react-dropzone';
import { DesignUpload, Upload } from '@/types';
import { TrackingFeatureTypes, trackFeature } from '@/utils/adobeLaunch';
import { fetchDesignUpload, uploadImage } from '@/utils/http/uploadImage';
import { PollingState, poll } from '@/utils/poll';
import { isEmbroideryMode } from '@/utils/params';
import { getImgBaseUrl } from '@/utils/imageUrls/getImgBaseUrl';

const buildEmbroideryConvertUrl = () => {
  return (
      '/image-server/v1/previews/embroidery?widthMm=210&heightMm=210&maxColors=8&minLineWidthMm=2&applyPatterns=true'
  );
};

const RASTER_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp'];

const getExtension = (name?: string) => (name ? name.split('.').pop()?.toLowerCase() : undefined);
const stripExtension = (name: string) => name.replace(/\.[^.]+$/, '');

const isPixelFile = (file: File) => {
  const ext = getExtension(file.name);
  return !!ext && RASTER_EXTENSIONS.includes(ext);
};

async function convertForEmbroideryIfNeeded(originalFile: File, signal?: AbortSignal): Promise<File> {
  if (!isEmbroideryMode()) return originalFile;
  if (!isPixelFile(originalFile)) return originalFile; // only convert raster images

  const form = new FormData();
  form.append('file', originalFile, originalFile.name);

  const response = await fetch(buildEmbroideryConvertUrl(), { method: 'POST', body: form, signal });
  if (!response.ok) {
    throw new Error(`Embroidery conversion failed (${response.status})`);
  }
  const blob = await response.blob();
  const base = stripExtension(originalFile.name);
  const convertedName = `${base}.png`;
  // Ensure resulting blob has correct type
  return new File([blob], convertedName, { type: 'image/png' });
}

const allSettled = (promises: Promise<any>[]) => {
  const totalUploads = promises.length;
  let successUploads = promises.length;
  return Promise.all(promises.map((promise) => promise.catch(() => successUploads--))).then(() => ({
    successUploads,
    totalUploads,
  }));
};

function checkDesignUpload(upload: DesignUpload): PollingState {
  switch (upload.lifecycleState) {
    case 'FINAL':
      return PollingState.SUCCESS;
    case 'INITIALIZED':
    case 'VALID':
      return PollingState.RUNNING;
    default:
      return PollingState.FAILED;
  }
}

export async function pollImageUpload(designUpload: DesignUpload, signal?: AbortSignal) {
  if (checkDesignUpload(designUpload) === PollingState.SUCCESS) {
    return designUpload;
  }
  return poll<DesignUpload>(() => fetchDesignUpload(designUpload.id, signal), checkDesignUpload);
}

export function uploadDesignsWithTracking(
  uploads: Upload[],
  rejectedFiles: FileRejection[],
  signal: AbortSignal
) {
  trackFeature('upload-start', { count: uploads.length }, true, TrackingFeatureTypes.EVENT);

  const designUploads = uploads.map((upload) => {
    // Embroidery conversion path
    return convertForEmbroideryIfNeeded(upload.file, signal)
      .then((fileForUpload) => {
        const data = new FormData();
        data.append('filedata', fileForUpload, fileForUpload.name);
        return uploadImage(data, signal);
      });
  });

  const designsReady = designUploads.map((pendingDesignUpload) =>
    pendingDesignUpload.then((designUpload) =>
      designUpload.designId ? designUpload : pollImageUpload(designUpload, signal)
    )
  );

  allSettled(designUploads)
    .then(() => allSettled(designsReady))
    .then(({ successUploads, totalUploads }) => {
      let failedUploads = totalUploads - successUploads;
      let cancelledUploads = 0;

      if (signal.aborted) {
        cancelledUploads = failedUploads;
        failedUploads = 0;
      }

      trackFeature(
        'upload-available',
        {
          failed: failedUploads,
          success: successUploads,
          invalid: rejectedFiles.length,
          cancelled: cancelledUploads,
        },
        true,
        TrackingFeatureTypes.EVENT
      );
    });

  return designsReady;
}

export async function uploadDesign(file: File | Blob, fileName?: string) {
  let fileForUpload: File | Blob = file;
  // Only attempt conversion if we have a File instance (Blob might not have a name)
  if (file instanceof File) {
    fileForUpload = await convertForEmbroideryIfNeeded(file);
  }

  const data = new FormData();
  // Prefer converted file's name if conversion happened; else provided fileName; else original name (if File)
  const finalName = fileForUpload instanceof File
    ? fileForUpload.name
    : fileName || (file instanceof File ? file.name : undefined);
  data.append('filedata', fileForUpload, finalName);

  let imageUpload = await uploadImage(data);
  if (!imageUpload.designId) {
    imageUpload = await pollImageUpload(imageUpload);
  }

  return imageUpload;
}
